import CiroModal from "../shared/CiroModal";
import classNames from "classnames";
import XIcon from "../../assets/img/icons/XIcon";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import { useCallback, useState } from "react";
import CiroCreateableTextInput from "../shared/CiroCreateableTextInput";
import { gql, useMutation } from "@apollo/client";
import {
  TeamMember_CreateInvitesMutation,
  TeamMember_CreateInvitesMutationVariables,
  TeamMember_CreateReferralsMutation,
  TeamMember_CreateReferralsMutationVariables,
  UserInviteStatus,
} from "../../__generated__/graphql";
import { UserPlusIcon, UsersIcon } from "@heroicons/react/24/outline";
import { PRICING_PAGE_LINK } from "../../utils/consts";
import CiroLink from "../shared/CiroLink";
import toast from "react-hot-toast";
import { Team_UserInvite } from "./TeamMembersTable";

const TeamMember_createInvites = gql`
  mutation TeamMember_createInvites($emails: [String!]!) {
    createInvites(emails: $emails) {
      successful {
        id
        email
      }
      failed {
        email
      }
      error
    }
  }
`;

const TeamMember_createReferrals = gql`
  mutation TeamMember_createReferrals($emails: [String!]!) {
    createReferrals(emails: $emails) {
      successful {
        email
      }
      failed {
        email
      }
      error
    }
  }
`;

interface ITeamMemberInviteReferModal {
  canInviteUsers?: boolean | null;
  overInviteLimit?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

interface IHandleRespose {
  data: {
    successful: { email: string }[];
    failed: { email: string }[];
    error?: string | null;
  };
  type: "invite" | "referral";
}

export const TeamMemberInviteReferModal = ({
  canInviteUsers,
  overInviteLimit,
  isOpen,
  onClose,
}: ITeamMemberInviteReferModal) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [tabToShow, setTabToShow] = useState<"invite" | "refer">("invite");

  const handleResponse = useCallback(
    ({ data, type }: IHandleRespose) => {
      const { failed, successful, error } = data;
      const successfulCount = successful.length;

      if (failed.length > 0) {
        let errorString = "";
        if (successfulCount === 1) {
          errorString = `1 ${type} was sent, but the following email failed: `;
        } else if (successfulCount > 1) {
          errorString = `${successfulCount} ${type}s were sent, but the following emails failed: `;
        } else {
          errorString = `Failed to send ${type}s for the following emails: `;
        }
        errorString += data.failed.map((item) => item.email).join(", ");
        if (error) {
          errorString += `. ${error}`;
        }
        setError(errorString);
        return;
      }

      if (successfulCount > 0) {
        setEmails([]);
        onClose();
        toast.success(`${successfulCount} ${type}s sent successfully.`);
        return;
      }

      setError(data.error ?? `Failed to create ${type}s`);
    },
    [onClose],
  );

  const [createInvites, { loading: inviteLoading }] = useMutation<
    TeamMember_CreateInvitesMutation,
    TeamMember_CreateInvitesMutationVariables
  >(TeamMember_createInvites, {
    onError: () => {
      setError("Failed to create invites");
    },
    onCompleted: (data) =>
      handleResponse({ data: data.createInvites, type: "invite" }),
  });

  const [createReferrals, { loading: referralLoading }] = useMutation<
    TeamMember_CreateReferralsMutation,
    TeamMember_CreateReferralsMutationVariables
  >(TeamMember_createReferrals, {
    onError: () => {
      setError("Failed to create referrals");
    },
    onCompleted: (data) =>
      handleResponse({ data: data.createReferrals, type: "referral" }),
  });

  const onSubmit = async () => {
    setError(undefined);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((e) => !emailRegex.test(e));

    if (invalidEmails.length > 0) {
      setError(`These emails are invalid: ${invalidEmails.join(", ")}`);
      return;
    }
    if (tabToShow === "invite") {
      createInvites({
        variables: {
          emails,
        },
        refetchQueries: [
          {
            query: Team_UserInvite,
            variables: { input: { status: UserInviteStatus.Pending } },
          },
        ],
      });
    }
    if (tabToShow === "refer") {
      createReferrals({
        variables: {
          emails,
        },
      });
    }
  };

  return (
    <CiroModal isOpen={isOpen} onClose={onClose} size={"LARGE"}>
      <div
        className={classNames(
          "flex",
          "font-medium",
          "items-center",
          "justify-between",
        )}
      >
        <div className={classNames("text-lg", "font-medium")}>
          Invite or Refer
        </div>
        <span onClick={onClose} className={classNames("cursor-pointer")}>
          <XIcon />
        </span>
      </div>
      <div className={classNames("mt-4")}>
        <div className={classNames("flex", "mb-5", "w-full", "border-t-1")}>
          <CiroButton
            customClassName={classNames("flex", "w-full", "p-4", "border-b-2", {
              "border-orange-600": tabToShow === "invite",
            })}
            style={CiroButtonStyleEnum.UNSTYLED}
            onClick={() => setTabToShow("invite")}
            analyticsField="Team member invite tab"
          >
            <UsersIcon className="w-5 h-5 mr-1 inline-block" />
            Invite
          </CiroButton>
          <CiroButton
            customClassName={classNames("flex", "w-full", "p-4", "border-b-2", {
              "border-orange-600": tabToShow === "refer",
            })}
            style={CiroButtonStyleEnum.UNSTYLED}
            onClick={() => setTabToShow("refer")}
            analyticsField="Team member referral tab"
          >
            <UserPlusIcon className="w-5 h-5 mr-1 inline-block" />
            Refer
          </CiroButton>
        </div>
        {tabToShow === "invite" && (!canInviteUsers || overInviteLimit) ? (
          !canInviteUsers ? (
            <>
              <p className={classNames("mb-5", "text-gray-500")}>
                Team invites are only available on{" "}
                <CiroLink href={PRICING_PAGE_LINK}>paid plans</CiroLink>. To
                give someone access to Ciro, use the Refer option, above.
              </p>
              <CiroButton
                customClassName={classNames("mt-8", "w-full")}
                style={CiroButtonStyleEnum.LOUD}
                analyticsField="Upgrade plan from invite modal"
                href={PRICING_PAGE_LINK}
              >
                Upgrade Plan
              </CiroButton>
            </>
          ) : (
            overInviteLimit && (
              <>
                <p className={classNames("mb-5", "text-gray-500")}>
                  You have reached the maximum number of team members allowed on
                  your current plan. To invite more team members, please upgrade
                  your plan.
                </p>
                <CiroButton
                  customClassName={classNames("mt-8", "w-full")}
                  style={CiroButtonStyleEnum.LOUD}
                  analyticsField="Upgrade plan from invite modal"
                  href={PRICING_PAGE_LINK}
                >
                  Upgrade Plan
                </CiroButton>
              </>
            )
          )
        ) : (
          <>
            <p className={classNames("mb-5", "text-gray-500")}>
              {tabToShow === "invite"
                ? "Invite team members to your organization"
                : "Refer a friend to try Ciro"}
            </p>

            <CiroCreateableTextInput
              label={
                <div className={classNames("text-sm", "font-semibold")}>
                  Emails
                </div>
              }
              value={emails}
              placeholder="Enter email addresses"
              setValue={(newValue) => {
                setEmails(newValue);
                setError(undefined);
              }}
              error={error}
            />
            {!error && (
              <p className={classNames("text-sm", "mt-1", "text-gray-400")}>
                Press TAB after each email to add it to the list.
              </p>
            )}
            <CiroButton
              customClassName={classNames("mt-8", "w-full")}
              style={CiroButtonStyleEnum.LOUD}
              analyticsField={
                tabToShow === "invite"
                  ? "Send team member invites"
                  : "Send team member referrals"
              }
              onClick={onSubmit}
              disabled={inviteLoading || referralLoading || emails.length === 0}
            >
              {tabToShow === "invite" &&
                (inviteLoading ? "Sending Invite..." : "Send Invite")}
              {tabToShow === "refer" &&
                (referralLoading ? "Sending Referral..." : "Send Referral")}
            </CiroButton>
          </>
        )}
      </div>
    </CiroModal>
  );
};
